import { InfoTooltip } from '@/components/InfoTooltip'
import type { Firm, ProductTypesFile } from '@/types/bundle'
import { entityLabel } from '../overrideUtils'

export interface EnabledProductsEditorProps {
  firm: Firm
  productTypes: ProductTypesFile
  onChange: (enabledProducts: string[]) => void
}

// Deliberate deviation from Frontend PRD 06's original "read-only, not
// editable from this screen" text for enabled_products: confirmed no other
// screen in this app can set it either (`grep -rln "enabled_products" src/`),
// so a brand-new firm was otherwise permanently stuck with zero products
// with no path through the GUI to fix that. Product enablement is a
// first-class field on the firm object itself (schema-validated directly,
// not one of the allow-listed override paths), so editing it here doesn't
// touch the Override Engine's own governance surface at all.
export function EnabledProductsEditor({ firm, productTypes, onChange }: EnabledProductsEditorProps) {
  const productIds = Object.keys(productTypes.product_types)

  function toggle(productId: string) {
    const next = firm.enabled_products.includes(productId)
      ? firm.enabled_products.filter((id) => id !== productId)
      : [...firm.enabled_products, productId]
    onChange(next)
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
        Enabled products
        <InfoTooltip text="Which products this firm can use. Unchecked products aren't available to them." />
      </span>
      {productIds.length === 0 ? (
        <span className="text-meta text-text-disabled">No product types defined yet</span>
      ) : (
        <div className="flex flex-col gap-1">
          {productIds.map((productId) => (
            <label key={productId} className="flex items-center gap-2 text-body text-text-primary">
              <input
                type="checkbox"
                checked={firm.enabled_products.includes(productId)}
                onChange={() => toggle(productId)}
                className="accent-accent"
              />
              {entityLabel('product_types', productId, productTypes)}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}
